import { Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';
import { copyToClipboard } from '../../utils/copyToClipboard';
import { Tooltip } from './Tooltip';

// CopyButton — atajo de copiado de un clic junto a un identificador (seguimiento, venta,
// folio, patente…). Seleccionar el texto a mano fila por fila es lento y en móvil casi
// imposible, y esos datos se copian todo el día para buscar o pegar en WhatsApp/correo.
//
// Frena la propagación (y la acción por defecto): casi siempre vive dentro de una fila o
// tarjeta clickeable, y copiar no debe abrir además el detalle ni seguir un enlace. Lo
// mismo con Enter/Espacio: las filas navegables escuchan keydown y, sin este freno,
// activar el botón con teclado navegaba en vez de copiar.
// Sin `value` no se renderiza: un botón que copia "—" o un placeholder es peor que nada.
//
// Props:
//   value          texto a copiar (string o número)
//   label          aria-label y tooltip, p. ej. t('Copiar número de seguimiento')
//   copiedMessage  aviso de éxito, p. ej. t('Número de seguimiento copiado')
//   className?     clases extra del botón
//   tabIndex?      para listas con roving tabindex (solo la fila activa es tabulable)
export function CopyButton({ value, label, copiedMessage, className, tabIndex }) {
  const { t } = useTranslation();
  const text = value == null ? '' : String(value).trim();
  if (!text) return null;

  return (
    <Tooltip content={label} describe={false}>
      <button
        type="button"
        aria-label={label}
        tabIndex={tabIndex}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
        }}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          copyToClipboard(text, copiedMessage, t);
        }}
        // p-1 -m-0.5: agranda el área tocable (~24px) sin mover la línea de texto.
        // text-fg-muted y no fg-subtle: el gris más claro (2,7:1 sobre blanco) apenas se veía
        // junto al número; fg-muted pasa el 3:1 que pide un ícono accionable.
        className={cn(
          'inline-flex items-center -m-0.5 rounded p-1 align-middle text-fg-muted transition-colors hover:text-brand-600 hover:bg-bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
          className,
        )}
      >
        <Copy className="w-3 h-3" aria-hidden="true" />
      </button>
    </Tooltip>
  );
}
